import Fuse, { FuseOptionKey, IFuseOptions, RangeTuple } from "fuse.js";
import { assocPath } from "ramda";
import { isNotNil } from "ramda-adjunct";
import { useMemo } from "react";

const fuseOptions: IFuseOptions<any> = {
  // isCaseSensitive: false,
  includeScore: true,
  shouldSort: true,
  includeMatches: true,
  // findAllMatches: false,
  minMatchCharLength: 3,
  // location: 0,
  threshold: 0.2,
  //distance: 0,
  //useExtendedSearch: true,
  ignoreLocation: false,
  // ignoreFieldNorm: false,
  // fieldNormWeight: 1,
};

export const useSearch = <T,>(list: readonly T[], keys: FuseOptionKey<T>[]) => {
  // Create a new Fuse instance with the provided list and keys
  const fuse = useMemo(() => new Fuse(list, { ...fuseOptions, keys }), [keys, list]);

  // Return a function that performs the search
  return (query: string) =>
    // Search for the query and process results
    fuse.search(query).map(({ item, matches }) => {
      // Create an array of objects with the highlighted text and path information
      const searchedItems =
        matches?.map(({ indices, value, key, refIndex }) => {
          // Process the matched text to highlight matching parts
          const fullIndices = value ? splitByIndices(value, indices) : value;
          // Create a path for placing the highlighted text in the result object
          const path = [...(key ?? []), refIndex ?? null].filter(isNotNil);
          return { fullIndices, path };
        }) ?? [];

      // Place all highlighted text in the appropriate locations in the result object
      return searchedItems.reduce((result, { path, fullIndices }) => assocPath(path, fullIndices, result), item);
    });
};

export type SearchedParts = {
  part: string;
  inRange: boolean;
}[];

const splitByIndices = (string: string, indices: readonly RangeTuple[]): SearchedParts => {
  // If there are no more indices to process, return the whole string as not in range
  if (!indices.length || indices[0] == null) {
    return [{ part: string, inRange: false }];
  }

  const [start, end] = indices[0];

  // Split the string into three parts: before match, matched text, and remaining text
  const beforeMatch = { part: string.substring(0, start), inRange: false };
  const matchedText = { part: string.substring(start, end + 1), inRange: true };

  // Process remaining text recursively with remaining indices
  const remainingParts = splitByIndices(string.substring(end + 1), indices.slice(1));

  // Combine parts and filter out empty strings
  return [beforeMatch, matchedText, ...remainingParts].filter(({ part }) => part.length > 0);
};
